/**
 * i18n Configuration
 * Language is determined by: user preference (localStorage) > browser language > env var > en-GB default.
 * Users can change their language in Account Settings; the preference persists
 * in localStorage (immediate) and DynamoDB (cross-device sync).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enGB from './locales/en-GB.json';
import enUS from './locales/en-US.json';
import it from './locales/it.json';
import es from './locales/es.json';

export const LANGUAGE_PREFERENCE_KEY = 'systemdox_language_preference';

export const SUPPORTED_LANGUAGES = [
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'en-US', label: 'English (US)' },
  { code: 'it', label: 'Italiano' },
  { code: 'es', label: 'Español' },
] as const;

export type SupportedLanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

function getSavedLanguage(): string | null {
  try {
    const saved = localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
    if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) {
      return saved;
    }
  } catch {
    // localStorage may be unavailable
  }
  return null;
}

function detectBrowserLanguage(): string | null {
  try {
    const browserLangs = navigator.languages ?? [navigator.language];
    for (const lang of browserLangs) {
      // Exact match (e.g. "en-GB", "it")
      const exact = SUPPORTED_LANGUAGES.find(l => l.code === lang);
      if (exact) return exact.code;
      // Prefix match (e.g. "it-IT" → "it", "es-MX" → "es")
      const prefix = lang.split('-')[0];
      const partial = SUPPORTED_LANGUAGES.find(l => l.code === prefix || l.code.startsWith(prefix + '-'));
      if (partial) return partial.code;
    }
  } catch {
    // navigator may be unavailable in SSR
  }
  return null;
}

const locale = getSavedLanguage() || detectBrowserLanguage() || import.meta.env.REACT_APP_LOCALE || 'en-GB';

i18n.use(initReactI18next).init({
  resources: {
    'en-GB': { translation: enGB },
    'en-US': { translation: enUS },
    'it': { translation: it },
    'es': { translation: es },
  },
  lng: locale,
  fallbackLng: 'en-GB',
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

/**
 * Change the active language and persist the preference to localStorage.
 */
export function setLanguagePreference(languageCode: SupportedLanguageCode): void {
  try {
    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, languageCode);
  } catch {
    // localStorage may be unavailable
  }
  i18n.changeLanguage(languageCode);
}

/**
 * Get the currently saved language code from localStorage.
 */
export function getLanguagePreference(): string {
  return getSavedLanguage() || detectBrowserLanguage() || import.meta.env.REACT_APP_LOCALE || 'en-GB';
}

export default i18n;
